<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';
import {
  cardParticles,
  compactXp,
  hexAlpha,
  mixColor,
  type LevelTier,
} from '@/utils/levels';

const props = defineProps<{
  tier: LevelTier;
  levelLabel: string;
  progress: number;
  xpToNext: number;
  isMaxLevel: boolean;
  playerName: string;
  playerId: string;
  avatar: string;
  nextLevelLabel: string;
}>();

// O card puxa toda a paleta da cor do elo — é o que faz Bronze e Diamante
// parecerem cartões diferentes sem duplicar markup.
const cardBg = computed(
  () =>
    `linear-gradient(168deg,${mixColor(props.tier.color, '#05091A', 0.2)} 0%,${mixColor(
      props.tier.color,
      '#05091A',
      0.58,
    )} 46%,${mixColor(props.tier.color, '#05091A', 0.9)} 100%)`,
);

// Borda viva de 1.4px: gradiente que atravessa a cor do elo e o acento.
const strokeGradient = computed(
  () =>
    `linear-gradient(100deg,rgba(255,255,255,.1) 0%,#D8DEE9 12%,${props.tier.color} 30%,rgba(255,255,255,.14) 46%,${props.tier.accent} 62%,#EAEEF5 78%,rgba(255,255,255,.1) 100%)`,
);

const avatarBg = computed(
  () =>
    `radial-gradient(circle at 50% 34%,${mixColor(props.tier.color, '#050C22', 0.1)} 0%,${mixColor(
      props.tier.color,
      '#050C22',
      0.48,
    )} 46%,#050A18 100%)`,
);

/** Pontinhos que pulsam dentro do cartão, na cor do elo. */
const particles = computed(() => cardParticles(props.tier));

const progressLabel = computed(() =>
  props.isMaxLevel
    ? 'Nível máximo alcançado'
    : `Para o ${props.nextLevelLabel.toLowerCase()}`,
);

const gapLabel = computed(() =>
  props.isMaxLevel
    ? 'Topo da escala'
    : `Faltam ${compactXp(props.xpToNext)} XP`,
);

// Plaquinha do ID copia o número puro (sem o "#"), igual ao dropdown de perfil
// do header — é o valor que se cola na transferência.
const copied = ref(false);

let copiedTimer: ReturnType<typeof setTimeout> | undefined;

async function copyId() {
  const raw = props.playerId.replace('#', '');

  if (!(await copyToClipboard(raw))) return;

  copied.value = true;
  clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    copied.value = false;
  }, 2000);
}

onBeforeUnmount(() => clearTimeout(copiedTimer));
</script>

<template>
  <div
    class="levels-stroke relative mx-auto w-full max-w-[430px] rounded-[14.4px] p-[1.4px]"
    :style="{
      background: strokeGradient,
      backgroundSize: '280% 100%',
      boxShadow: `0 14px 30px -24px ${hexAlpha(tier.color, 0.7)}`,
    }"
  >
    <div
      class="relative rounded-[13px]"
      :style="{ background: cardBg }"
    >
      <!-- Brilho de canto + partículas + badge fantasma (nesta ordem: a marca
           d'água do badge fica por cima dos pontinhos, como no protótipo) -->
      <div
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 overflow-hidden rounded-[13px]"
      >
        <div
          class="absolute inset-0"
          style="
            background: radial-gradient(
              120% 100% at 6% 0%,
              rgba(255, 255, 255, 0.06),
              transparent 58%
            );
          "
        ></div>
        <span
          v-for="(particle, index) in particles"
          :key="index"
          class="levels-pop absolute rounded-full"
          :style="{
            left: particle.left,
            top: particle.top,
            width: particle.size,
            height: particle.size,
            background: particle.color,
            boxShadow: `0 0 ${particle.blur} ${particle.color}`,
            animationDuration: particle.duration,
            animationDelay: particle.delay,
          }"
        ></span>
        <div
          class="absolute left-1/2 top-1/2 h-[220px] w-[220px] -translate-x-1/2 -translate-y-1/2 bg-contain bg-center bg-no-repeat opacity-[0.05]"
          :style="{ backgroundImage: `url(${tier.badge})` }"
        ></div>
      </div>

      <div
        class="relative z-[1] flex items-center gap-2 px-[11px] pt-4 lg:px-[15px]"
      >
        <div class="flex min-w-0 flex-1 flex-col gap-px">
          <span
            class="truncate text-[13px] font-bold leading-[17px] tracking-[-0.015em] text-white"
          >
            {{ levelLabel }}
          </span>
          <span
            class="truncate text-[11.5px] font-medium leading-4 text-white/[0.78]"
          >
            {{ tier.name }}
          </span>
        </div>

        <!-- Badge do elo estourando para fora do card, com o avatar embutido -->
        <span
          class="pointer-events-none relative -mx-1.5 -mt-[62px] flex h-[104px] w-[104px] flex-none items-center justify-center lg:h-[124px] lg:w-[124px]"
        >
          <span
            class="absolute left-1/2 top-[52%] h-[77px] w-[77px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-[9px] lg:h-[92px] lg:w-[92px]"
            :style="{
              background: `radial-gradient(${hexAlpha(tier.color, 0.3)},transparent 66%)`,
            }"
          ></span>
          <span
            class="absolute inset-0 bg-contain bg-center bg-no-repeat"
            :style="{
              backgroundImage: `url(${tier.badge})`,
              filter: 'drop-shadow(0 4px 14px rgba(0,0,0,.55))',
            }"
          ></span>
          <span
            class="relative z-[1] mt-[6%] h-[52px] w-[52px] rounded-full p-[2.5px] lg:h-[62px] lg:w-[62px]"
            :style="{
              background: `linear-gradient(140deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.38)})`,
              boxShadow: `0 0 18px ${hexAlpha(tier.color, 0.3)},0 6px 16px rgba(0,0,0,.45)`,
            }"
          >
            <span
              class="block h-full w-full rounded-full bg-[length:88%_auto] bg-bottom bg-no-repeat"
              :style="{
                background: avatarBg,
                backgroundImage: `url(${avatar})`,
                boxShadow: `inset 0 0 16px ${hexAlpha(tier.color, 0.5)}`,
              }"
            ></span>
          </span>
        </span>

        <div class="flex min-w-0 flex-1 flex-col items-end gap-px">
          <span
            class="truncate text-[13px] font-bold leading-[17px] tracking-[-0.015em] text-white"
          >
            {{ isMaxLevel ? 'Nível máximo' : 'Próximo nível' }}
          </span>
          <span
            class="truncate text-[11.5px] font-medium leading-4 text-white/[0.78]"
          >
            {{ gapLabel }}
          </span>
        </div>
      </div>

      <div
        class="relative z-[1] flex flex-wrap items-center justify-center gap-[7px] px-[11px] pt-1 lg:px-[15px]"
      >
        <span
          class="text-[14px] font-bold leading-[19px] tracking-[-0.015em] text-white"
        >
          {{ playerName }}
        </span>
        <button
          v-if="playerId"
          aria-label="Copiar ID"
          class="relative flex items-center gap-1 whitespace-nowrap rounded-full bg-[rgba(0,6,20,0.42)] px-2 py-[3px] text-[9.5px] font-semibold leading-[13px] tracking-[0.07em] text-white/[0.72] tabular-nums shadow-[inset_0_0_0_1px_rgba(255,255,255,0.12)]"
          type="button"
          @click="copyId"
        >
          ID {{ playerId }}
          <NuxtIcon
            class="text-[10px] tracking-normal"
            name="copy"
          />
          <span
            v-if="copied"
            class="levels-copied absolute left-1/2 top-[calc(100%+4px)] -translate-x-1/2 rounded bg-[#00C9FF] px-1 py-0.5 text-[8px] font-medium tracking-normal text-white"
          >
            Copiado
          </span>
        </button>
      </div>

      <div
        class="relative z-[1] flex flex-col gap-[5px] px-[11px] pb-3 pt-[10px] lg:px-[15px]"
      >
        <div class="flex items-baseline justify-between gap-2">
          <span
            class="whitespace-nowrap text-[10.5px] font-semibold leading-[14px] tracking-[0.05em] text-white/[0.62]"
          >
            {{ progressLabel }}
          </span>
          <span
            class="whitespace-nowrap text-[12px] font-bold leading-[15px] text-white tabular-nums"
          >
            {{ Math.round(progress) }}%
          </span>
        </div>
        <div
          class="h-[6px] overflow-hidden rounded-full bg-[rgba(0,5,20,0.6)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.5)]"
        >
          <div
            class="levels-bar h-[6px] rounded-full"
            :style="{
              width: `${progress}%`,
              background: `linear-gradient(90deg,${tier.color},${mixColor(tier.color, '#ffffff', 0.42)})`,
              boxShadow: `0 0 8px ${hexAlpha(tier.color, 0.5)}`,
            }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
/* O reset global mata `transition`, então a borda viva anda por keyframes. */
@keyframes levelsStrokeFlow {
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 280% 50%;
  }
}

.levels-stroke {
  animation: levelsStrokeFlow 7s linear infinite;
}

/* Pontinhos dentro do cartão: aparecem subindo um pouco e somem. */
@keyframes levelsPop {
  0%,
  100% {
    opacity: 0;
    transform: translateY(4px) scale(0.6);
  }
  45% {
    opacity: 0.9;
    transform: translateY(-5px) scale(1);
  }
}

.levels-pop {
  opacity: 0;
  animation-name: levelsPop;
  animation-timing-function: ease-in-out;
  animation-iteration-count: infinite;
}

@keyframes levelsBarGrow {
  0% {
    transform: scaleX(0);
  }
  100% {
    transform: scaleX(1);
  }
}

.levels-bar {
  transform-origin: left center;
  animation: levelsBarGrow 0.9s cubic-bezier(0.34, 1.2, 0.4, 1) both;
}

@keyframes levelsCopiedIn {
  0% {
    opacity: 0;
    transform: translate(-50%, -3px);
  }
  100% {
    opacity: 1;
    transform: translate(-50%, 0);
  }
}

.levels-copied {
  animation: levelsCopiedIn 0.18s ease-out both;
}
</style>
